import { equals, isEmpty, pipe, uniq } from 'ramda';
import { compact } from 'ramda-adjunct';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  TagsActionsClickOrigins,
  TagsActionsTargets,
  useTrackTagsActions,
} from '../../../../../../utils/hooks/bi/useTrackTagsActions';
import { useBoolean } from '../../../../../../utils/hooks/useBoolean';
import { Text, TextButton } from '../../../../../../components/common';
import { PanelSection } from '../../../PanelSection';
import styles from './LabelsEditor.scss';
import { IOuter } from './LabelsEditor.types';
import { LabelsSelect } from './LabelsSelect';
import { LabelsViewer } from './LabelsViewer';

export const LabelsEditor: FC<IOuter> = ({
  labels: labelsFromProps,
  onLabelClick,
  readOnly = false,
  item,
  onLabelsSave,
}) => {
  const [labels, setLabels] = useState(pipe<string[], string[], string[]>(uniq, compact)(labelsFromProps));

  const { value: areLabelsEditing, setTrue: startLabelsEditing, setFalse: completeLabelsEditing } = useBoolean(false);

  const hasLabels = Boolean(labels.length);

  const { trackTagsActions } = useTrackTagsActions();

  const completeEditingAndSave = useCallback(() => {
    completeLabelsEditing();

    if (!equals(labelsFromProps, labels)) {
      onLabelsSave(labels);
    }
  }, [labelsFromProps, labels, onLabelsSave]);

  const handleLabelAdd = useCallback(
    (addedLabel, newLabels) => {
      setLabels(newLabels);

      trackTagsActions(
        {
          clickOrigin: TagsActionsClickOrigins.MoreInfo,
          tag: addedLabel,
          target: TagsActionsTargets.Added,
        },
        item,
      );
    },
    [setLabels, trackTagsActions, item],
  );

  const handleLabelRemove = useCallback(
    (removedLabel, newLabels) => {
      setLabels(newLabels);

      trackTagsActions(
        {
          clickOrigin: TagsActionsClickOrigins.MoreInfo,
          tag: removedLabel,
          target: TagsActionsTargets.Deleted,
        },
        item,
      );
    },
    [setLabels, trackTagsActions, item],
  );

  const header = (
    <Text weight="bold" size="small">
      <FormattedMessage id="labels-editor.labels" />
    </Text>
  );

  const editOrAddTagsButton = areLabelsEditing ? (
    <TextButton
      key="complete-labels-edit-button"
      as="a"
      size="tiny"
      weight="normal"
      onClick={completeEditingAndSave}
      dataHook="complete-editing-button"
    >
      <FormattedMessage id="labels-editor.done" />
    </TextButton>
  ) : (
    <TextButton
      key="start-labels-edit-button"
      as="a"
      disabled={readOnly}
      size="tiny"
      weight="normal"
      onClick={startLabelsEditing}
      dataHook="add-labels-button"
    >
      <FormattedMessage id={isEmpty(labels) ? 'labels-editor.add-labels' : 'labels-editor.edit-labels'} />
    </TextButton>
  );

  const labelsContent = areLabelsEditing ? (
    <LabelsSelect
      labels={labels}
      maxNumRows={3}
      onLabelAdd={handleLabelAdd}
      onLabelRemove={handleLabelRemove}
      onClickOutside={completeEditingAndSave}
      className={styles.labelsSelect}
      dataHook="labels-select"
    />
  ) : hasLabels ? (
    <>
      <div className={styles.labelsViewerWrapper}>
        <LabelsViewer labels={labels} onLabelClick={onLabelClick} dataHook="labels-viewer" />
      </div>
    </>
  ) : (
    <div className={styles.noLabelsMessage} data-hook="no-labels-message" onClick={startLabelsEditing}>
      <Text size="tiny">
        <FormattedMessage id="labels-editor.no-labels-message" />
      </Text>
    </div>
  );

  return (
    <PanelSection header={header} collapsed>
      <div className={styles.labelsSelectNewLayout}>{labelsContent}</div>
      {editOrAddTagsButton}
    </PanelSection>
  );
};
